import { useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { ISelectedPoint } from '@d-orca/shared/trusstor';
import { sanitizeSvg } from '@utils/sanitizeSvg';
import { renderedPlanRect, type IScreenRect } from '@utils/svgMarkers';
import {
  cleanSvgAreaIds,
  findAreaElement,
  getAreaIdFromElementId,
  highlightActiveArea,
  paintSelectedAreas,
  unionCenterPercent,
} from './floorPlanSvg';
import styles from './FloorPlanCanvas.module.css';

/** A dropped pin is the shared selected-point shape (`{ xPercentage, yPercentage }`). */
export type Pin = ISelectedPoint;

/** An area's id paired with the name shown when it's the active/hovered area. */
export interface IAreaLabel {
  areaId: string;
  label: string;
}

interface IFloorPlanCanvasBaseProps {
  /** Raw SVG markup from trusstor's storage proxy. */
  svg: string;
  /** Pins keyed by areaId — each entry renders as a marker over the SVG. */
  pinsByArea: Record<string, Pin[]>;
  /**
   * Areas that should display the highlight tint, regardless of pin presence.
   * Includes both areas selected via the List tab and areas that have at
   * least one pin (the caller is responsible for the union).
   */
  selectedAreaIds: ReadonlySet<string>;
}

/** Editable canvas: tapping an area polygon drops a pin. */
interface IFloorPlanCanvasEditProps extends IFloorPlanCanvasBaseProps {
  onAddPin: (areaId: string, pin: Pin) => void;
  areaLabels?: never;
  activeAreaId?: never;
  onActiveAreaChange?: never;
}

/**
 * Read-only canvas: no pins are added; hovering/tapping an area highlights it
 * and reveals its name (`areaLabels`). A tap still reports the tapped area via
 * `onActiveAreaChange` (touch parity with hover).
 */
interface IFloorPlanCanvasViewProps extends IFloorPlanCanvasBaseProps {
  onAddPin?: never;
  /** Name labels for the areas — the *active* area's label is revealed at its centre. */
  areaLabels?: ReadonlyArray<IAreaLabel>;
  /** Currently highlighted area (controlled). Darkens the polygon + shows its label. */
  activeAreaId?: string | null;
  /** Fired when hovering/tapping a selected area (or leaving it → null). */
  onActiveAreaChange?: (areaId: string | null) => void;
}

// A union so the two modes can't be mixed: passing an edit prop alongside a
// view prop (or vice-versa) fails to typecheck.
type IFloorPlanCanvasProps = IFloorPlanCanvasEditProps | IFloorPlanCanvasViewProps;

/** The active area's name and its centre, as a % of the rendered plan box. */
interface IActiveLabel {
  label: string;
  xPercentage: number;
  yPercentage: number;
}

/** The injected floor-plan `<svg>` under the canvas wrapper, or null. */
function svgRootOf(container: HTMLElement | null): SVGSVGElement | null {
  return container?.querySelector<SVGSVGElement>('svg') ?? null;
}

/**
 * Renders the floor-plan SVG with tappable area polygons. Tapping appends a
 * new pin to the area at the exact tap location (no de-duplication — multiple
 * pins per area are intentional, see trusstor_dashboard's
 * FloorPlanPointSelector). When `onAddPin` is omitted the canvas is read-only.
 *
 * Pin coordinates are percentages of the *rendered plan* — the painted viewBox
 * region (`renderedPlanRect`), not the `<svg>` element box and not the
 * container. The plan is aspect-preserved, so it letterboxes both inside the
 * canvas AND inside the `<svg>` element when that element's box has a different
 * aspect ratio than the viewBox; anchoring pins to the measured plan rect keeps
 * them on the same plan feature at any container size or shape (editor popper,
 * drawer companion, on resize, …).
 */
export function FloorPlanCanvas({
  svg,
  pinsByArea,
  selectedAreaIds,
  onAddPin,
  areaLabels,
  activeAreaId,
  onActiveAreaChange,
}: IFloorPlanCanvasProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  // The rendered plan's box relative to the canvas — the frame the pins live in.
  const [planBox, setPlanBox] = useState<IScreenRect | null>(null);
  // The hovered/active area's name + centre, revealed on the plan.
  const [activeLabel, setActiveLabel] = useState<IActiveLabel | null>(null);
  // The previously-darkened area, so the darken can be moved without a full repaint.
  const previousActiveRef = useRef<string | null>(null);

  // Injected once per SVG. A stable object keeps React from re-setting the
  // wrapper's innerHTML on unrelated re-renders — which would wipe the area fill
  // the paint effects apply imperatively below (see FloorPlanCanvas.test.tsx).
  const svgMarkup = useMemo(() => ({ __html: sanitizeSvg(svg) }), [svg]);

  // Base selection tint. Keyed only on the SVG and the selection — the
  // active-area darken is a separate, cheaper effect below so hovering doesn't
  // repaint the whole plan. `useLayoutEffect` runs before the browser paints, so
  // the un-styled intermediate state is never visible.
  useLayoutEffect(() => {
    const root = svgRootOf(wrapRef.current);
    if (!root) return;
    cleanSvgAreaIds(root);
    paintSelectedAreas(root, selectedAreaIds);
  }, [svgMarkup, selectedAreaIds]);

  // Move the darken to the active area (reverting the previous one) and place its
  // name at the centre of its polygons. `highlightActiveArea` only touches those
  // two areas, so this is cheap at hover cadence, and returns the active area's
  // elements so we don't re-scan the SVG for the label. `planBox` is a dependency
  // so the label re-centres when the plan is re-measured (resize).
  useLayoutEffect(() => {
    const root = svgRootOf(wrapRef.current);
    if (!root) return;
    const activeElements = highlightActiveArea(
      root,
      activeAreaId ?? null,
      previousActiveRef.current,
      selectedAreaIds,
    );
    previousActiveRef.current = activeAreaId ?? null;

    const label =
      activeElements.length > 0 && activeAreaId
        ? areaLabels?.find((area) => area.areaId === activeAreaId)?.label
        : undefined;
    const planRect = renderedPlanRect(root);
    const centre = label && planRect ? unionCenterPercent(activeElements, planRect) : null;
    if (!label || !centre) {
      setActiveLabel(null);
      return;
    }
    const next: IActiveLabel = { label, ...centre };
    setActiveLabel((prev) =>
      prev &&
      prev.label === next.label &&
      prev.xPercentage === next.xPercentage &&
      prev.yPercentage === next.yPercentage
        ? prev
        : next,
    );
  }, [svgMarkup, selectedAreaIds, planBox, activeAreaId, areaLabels]);

  // Measure the rendered SVG box (relative to the canvas) and keep it in sync as
  // the container resizes, so the pin/label overlay tracks the plan rather than
  // the letterboxed container. Elements are captured once and reused by every
  // observer firing.
  useLayoutEffect(() => {
    const canvas = wrapRef.current;
    const svgEl = svgRootOf(canvas);
    if (!canvas || !svgEl) return;
    const measure = () => {
      const canvasRect = canvas.getBoundingClientRect();
      // The *rendered plan* rect, not the <svg> element box — the two differ
      // whenever the element box's aspect ratio doesn't match the viewBox, and
      // the plan then letterboxes inside it. Anchoring pins to the element box
      // would drift them into those empty bands (see renderedPlanRect).
      const planRect = renderedPlanRect(svgEl);
      if (!planRect) return;
      const next: IScreenRect = {
        left: planRect.left - canvasRect.left,
        top: planRect.top - canvasRect.top,
        width: planRect.width,
        height: planRect.height,
      };
      setPlanBox((prev) =>
        prev &&
        prev.left === next.left &&
        prev.top === next.top &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    observer.observe(svgEl);
    return () => observer.disconnect();
  }, [svg]);

  /** The selected area under `target`, or null. */
  function selectedAreaAt(target: EventTarget | null): string | null {
    const areaEl = findAreaElement(target as Element | null);
    const areaId = areaEl ? getAreaIdFromElementId(areaEl.id) : '';
    return areaId && selectedAreaIds.has(areaId) ? areaId : null;
  }

  /** Report the active area, de-duped against the current prop so a mousemove
   *  within the same area doesn't spam the parent — and so a chip-driven change
   *  isn't clobbered by a stale local value. */
  function reportActive(areaId: string | null) {
    if (!onActiveAreaChange || activeAreaId === areaId) return;
    onActiveAreaChange(areaId);
  }

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    if (onAddPin) {
      const areaEl = findAreaElement(e.target as Element | null);
      if (!areaEl) return;
      const areaId = getAreaIdFromElementId(areaEl.id);
      if (!areaId) return;

      // Percentages relative to the rendered plan (the painted viewBox, not the
      // <svg> element box), so the pin lands on the same plan feature at any
      // render size or container aspect ratio — matching how it's placed back.
      const svgEl = svgRootOf(wrapRef.current);
      const rect = svgEl ? renderedPlanRect(svgEl) : null;
      if (!rect) return;
      const xPercentage = ((e.clientX - rect.left) / rect.width) * 100;
      const yPercentage = ((e.clientY - rect.top) / rect.height) * 100;
      onAddPin(areaId, { xPercentage, yPercentage });
      return;
    }
    // Read-only viewer: tap-to-highlight for touch devices (no hover).
    reportActive(selectedAreaAt(e.target));
  }

  // Flatten pins for rendering and keep a stable React key per (areaId, index).
  const flatPins = useMemo(
    () =>
      Object.entries(pinsByArea).flatMap(([areaId, pins]) =>
        pins.map((p, i) => ({ key: `${areaId}-${i}`, ...p })),
      ),
    [pinsByArea],
  );

  return (
    <div
      ref={wrapRef}
      className={`${styles.canvas} ${onAddPin ? '' : styles.readOnly}`}
      onClick={handleClick}
      onMouseMove={onActiveAreaChange ? (e) => reportActive(selectedAreaAt(e.target)) : undefined}
      onMouseLeave={onActiveAreaChange ? () => reportActive(null) : undefined}
    >
      {/* SVG comes from the storage proxy. Admins upload these bytes —
          sanitise before inlining. See `sanitizeSvg` for the threat model.
          The prop object is memoised so React doesn't re-inject the SVG (which
          would wipe the imperatively-applied area fill) on every re-render. */}
      <div className={styles.svgWrap} dangerouslySetInnerHTML={svgMarkup} />
      {/* Pins live in an overlay that exactly covers the rendered plan, so their
          percentages map onto plan features regardless of container shape. */}
      {planBox && (
        <div
          className={styles.pinLayer}
          style={{ left: planBox.left, top: planBox.top, width: planBox.width, height: planBox.height }}
        >
          {/* Reveal only the active area's name — hover/tap identifies it in place. */}
          {activeLabel && (
            <span
              className={styles.areaLabel}
              style={{ left: `${activeLabel.xPercentage}%`, top: `${activeLabel.yPercentage}%` }}
            >
              {activeLabel.label}
            </span>
          )}
          {flatPins.map((pin) => (
            <span
              key={pin.key}
              className={styles.pin}
              style={{ left: `${pin.xPercentage}%`, top: `${pin.yPercentage}%` }}
              aria-hidden="true"
            >
              <PinSvg />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Map-pin teardrop. Centered horizontally with the tip at the click point
 *  via the wrapper's `transform: translate(-50%, -100%)`. */
function PinSvg() {
  return (
    <svg width="20" height="26" viewBox="0 0 20 26" fill="none" aria-hidden="true">
      <path
        d="M10 0C4.477 0 0 4.477 0 10c0 6.5 8.5 14.5 9.3 15.5.4.5 1 .5 1.4 0C11.5 24.5 20 16.5 20 10 20 4.477 15.523 0 10 0z"
        fill="var(--g800)"
      />
      <circle cx="10" cy="10" r="4" fill="var(--color-surface)" />
    </svg>
  );
}
